import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, FileX, ChevronRight, Trash2, Plus, TrendingUp, Linkedin, GitCompare, Check, X } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { listEvaluations, deleteEvaluation } from "@/lib/api";
import Footer from "@/components/Footer";

export default function History() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await listEvaluations());
    } catch (e) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await deleteEvaluation(id);
      setItems(items.filter((i) => i.id !== id));
      setSelected(selected.filter((s) => s !== id));
      toast.success("Audit deleted");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Delete failed");
    }
  };

  const toggleSelect = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selected.includes(id)) {
      setSelected(selected.filter((s) => s !== id));
    } else {
      if (selected.length >= 4) {
        toast.error("Max 4", { description: "Compare up to 4 audits at a time." });
        return;
      }
      setSelected([...selected, id]);
    }
  };

  const startCompare = () => {
    if (selected.length < 2) {
      toast.error("Pick at least 2 audits");
      return;
    }
    navigate(`/compare?ids=${selected.join(",")}`);
  };

  // Build chronological chart data (oldest -> newest)
  const chartData = [...items].reverse().map((it, idx) => ({
    n: idx + 1,
    score: it.overall_score,
    ats: it.ats_score,
    label: new Date(it.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  }));

  const avgScore = items.length ? Math.round(items.reduce((a, b) => a + (b.overall_score || 0), 0) / items.length) : 0;
  const bestScore = items.length ? Math.max(...items.map((i) => i.overall_score || 0)) : 0;
  const latestScore = items[0]?.overall_score || 0;
  const trend = items.length >= 2 ? items[0].overall_score - items[items.length - 1].overall_score : 0;

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 text-zinc-900" data-testid="history-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="overline mb-4">◷ ARCHIVE</div>
            <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tight">Past audits</h1>
          </div>
          <div className="flex items-center gap-2">
            {items.length >= 2 && (
              <button
                onClick={() => { setCompareMode(!compareMode); setSelected([]); }}
                data-testid="compare-toggle"
                className={`btn-sharp ${compareMode ? "bg-zinc-900 text-white" : "border border-zinc-300 hover:bg-zinc-900 hover:text-white"}`}
              >
                <GitCompare className="w-4 h-4" /> {compareMode ? "Cancel compare" : "Compare audits"}
              </button>
            )}
            <Link to="/evaluate" className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white" data-testid="new-audit-cta">
              <Plus className="w-4 h-4" /> New audit
            </Link>
          </div>
        </div>

        {compareMode && (
          <div className="mb-6 border border-[#DC1C22] bg-blue-50/40 p-4 flex items-center justify-between flex-wrap gap-3" data-testid="compare-toolbar">
            <div className="text-sm">
              <strong>{selected.length}</strong> selected · pick 2–4 audits to compare side-by-side
            </div>
            <button
              onClick={startCompare}
              disabled={selected.length < 2}
              data-testid="run-compare"
              className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white disabled:bg-zinc-300"
            >
              Compare {selected.length} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-16 text-center" data-testid="empty-state">
            <FileX className="w-12 h-12 mx-auto text-zinc-300 mb-4" strokeWidth={1.5} />
            <h3 className="font-display font-bold text-2xl mb-2">No audits yet</h3>
            <p className="text-zinc-500 mb-6">Run your first audit to see it here.</p>
            <Link to="/evaluate" className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white">Start an audit</Link>
          </div>
        ) : (
          <>
            {/* Stats + Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-zinc-200 border border-zinc-200 mb-12" data-testid="trend-block">
              <div className="lg:col-span-3 bg-white p-6 grid grid-rows-2 gap-4">
                <div>
                  <div className="overline mb-2">LATEST</div>
                  <div className="font-display font-extrabold text-4xl text-[#DC1C22]">{latestScore}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {trend !== 0 && (
                      <span className={trend > 0 ? "text-[#007A5E]" : "text-[#FF2A00]"}>
                        {trend > 0 ? "▲" : "▼"} {Math.abs(trend)} vs first
                      </span>
                    )}
                  </div>
                </div>
                <div className="border-t border-zinc-100 pt-4 grid grid-cols-2 gap-4">
                  <div>
                    <div className="overline mb-1">AVG</div>
                    <div className="font-display font-bold text-2xl">{avgScore}</div>
                  </div>
                  <div>
                    <div className="overline mb-1">BEST</div>
                    <div className="font-display font-bold text-2xl">{bestScore}</div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-9 bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="overline inline-flex items-center gap-2">
                    <TrendingUp className="w-3 h-3" /> SCORE TRENDS
                  </div>
                  <div className="flex items-center gap-4 text-xs text-zinc-500">
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#DC1C22]" /> Overall</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-zinc-400" /> ATS</span>
                  </div>
                </div>
                <div className="w-full h-[200px] min-w-0" data-testid="trend-chart">
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                    <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid stroke="#F4F4F5" strokeDasharray="0" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 11 }}
                        labelStyle={{ color: "#71717A", textTransform: "uppercase", letterSpacing: "0.1em" }}
                      />
                      <ReferenceLine y={80} stroke="#007A5E" strokeDasharray="2 4" strokeOpacity={0.4} />
                      <Line type="monotone" dataKey="ats" stroke="#A1A1AA" strokeWidth={1.5} dot={{ r: 2.5, fill: "#A1A1AA" }} />
                      <Line type="monotone" dataKey="score" stroke="#DC1C22" strokeWidth={2} dot={{ r: 3.5, fill: "#DC1C22" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* List */}
            <div className="border-t border-l border-zinc-200/70">
              <div className="grid grid-cols-12 border-r border-b border-zinc-200/70 bg-zinc-50">
                {compareMode && <div className="col-span-1 p-4"></div>}
                <div className={`${compareMode ? "col-span-4" : "col-span-5"} p-4 overline`}>Candidate / Role</div>
                <div className="col-span-2 p-4 overline">Date</div>
                <div className="col-span-2 p-4 overline">ATS</div>
                <div className="col-span-2 p-4 overline">Score</div>
                <div className="col-span-1 p-4"></div>
              </div>
              {items.map((it, idx) => {
                const v = it.overall_score || 0;
                const color = v >= 80 ? "#007A5E" : v >= 60 ? "#DC1C22" : v >= 40 ? "#FFBF00" : "#FF2A00";
                const isLinkedin = it.source_type === "linkedin";
                const isSelected = selected.includes(it.id);
                const Row = compareMode ? "div" : Link;
                const rowProps = compareMode
                  ? { onClick: (e) => toggleSelect(it.id, e), role: "button" }
                  : { to: `/results/${it.id}` };
                return (
                  <Row
                    key={it.id}
                    {...rowProps}
                    data-testid={`history-item-${idx}`}
                    className={`grid grid-cols-12 border-r border-b border-zinc-200/70 transition-colors group items-center cursor-pointer ${
                      isSelected ? "bg-blue-50" : "hover:bg-zinc-50"
                    }`}
                  >
                    {compareMode && (
                      <div className="col-span-1 p-4 flex justify-center">
                        <span
                          data-testid={`select-${idx}`}
                          className={`w-5 h-5 border-2 inline-flex items-center justify-center ${
                            isSelected ? "bg-[#DC1C22] border-[#DC1C22]" : "border-zinc-300"
                          }`}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </span>
                      </div>
                    )}
                    <div className={`${compareMode ? "col-span-4" : "col-span-5"} p-4`}>
                      <div className="font-medium flex items-center gap-2">
                        {isLinkedin && <Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" />}
                        {it.candidate_name || "Untitled"}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">{it.target_role || (isLinkedin ? "LinkedIn profile" : "No target role")}</div>
                    </div>
                    <div className="col-span-2 p-4 text-sm text-zinc-500">
                      {new Date(it.created_at).toLocaleDateString()}
                    </div>
                    <div className="col-span-2 p-4 font-mono tabular-nums">{it.ats_score}<span className="text-zinc-400 text-xs">/100</span></div>
                    <div className="col-span-2 p-4">
                      <span className="font-display font-extrabold text-2xl tabular-nums" style={{ color }}>{v}</span>
                    </div>
                    <div className="col-span-1 p-4 flex items-center justify-end gap-2">
                      {!compareMode && (
                        <>
                          <button onClick={(e) => remove(it.id, e)} data-testid={`delete-${idx}`} className="text-zinc-400 hover:text-red-600 p-1">
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-zinc-900" />
                        </>
                      )}
                    </div>
                  </Row>
                );
              })}
            </div>
          </>
        )}
      </div>
      <Footer />
    </main>
  );
}
